/* ==========================================================================
   Stream tile hover

   The in-tile hover first built for the PDP's "Related films to stream" row,
   applied to every stream film tile on the site (.product-card.stream-block:
   On Demand, film pages, homepage, in cinemas, search).

   Everything stays inside the tile: on hover the title and details slide
   up, the logline and buttons rise beneath them, a shade fades in and the
   still drifts in a touch. Transforms and opacity only, so nothing
   re-lays-out while it plays; leaving eases everything back from wherever
   it has got to. At rest the text block sits lower by the logline's height,
   so the title and details sit exactly where they always have; the logline
   waits, invisible, beneath them (clipped by the rounded image frame).

   The PDP's cards (snippets/pdp-film-card.liquid) already carry the shade
   and logline markup. Older tiles carry the logline and links in their
   dropdown panel (.additional-info-hover) instead, so the same markup is
   built from that panel and the panel is retired (stream-tile-hover.css).
   The whole tile is one link to the film page, so the buttons are spans
   that go to their own address (#shop, #in-cinemas) when clicked.

   Pointer devices with GSAP only, and not with reduced motion. Anywhere
   else the tiles keep their old behaviour.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  var ARROW = '<svg class="watch-now-arrow" viewBox="0 0 12 12" aria-hidden="true">' +
    '<line x1="2" y1="10" x2="10" y2="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '<polyline points="4 2 10 2 10 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Builds the shade and the logline + buttons block for a tile that only
  // has the old dropdown panel. Returns false when there's nothing to build.
  function buildExtra(card) {
    var image = card.querySelector('.card-image');
    var info = card.querySelector('.card-info');
    var panel = card.querySelector('.additional-info-hover');
    if (!image || !info || !panel) return false;

    var extra = document.createElement('div');
    extra.className = 'pdp-stream-extra';

    var loglineSource = panel.querySelector('.additional-info-hover-p');
    var logline = loglineSource ? loglineSource.textContent.trim() : '';
    if (logline) {
      var p = document.createElement('p');
      p.className = 'pdp-stream-logline';
      p.textContent = logline;
      extra.appendChild(p);
    }

    var links = panel.querySelectorAll('a[href]');
    if (links.length) {
      var row = document.createElement('div');
      row.className = 'pdp-stream-ctas';
      Array.prototype.forEach.call(links, function (link) {
        var label = link.textContent.trim();
        if (!label) return;
        var cta = document.createElement('span');
        cta.className = 'watch-now-link pdp-stream-cta';
        cta.setAttribute('data-href', link.getAttribute('href'));
        cta.innerHTML = '<span class="pdp-stream-cta-fill" aria-hidden="true"></span><span></span>' + ARROW;
        cta.children[1].textContent = label;
        row.appendChild(cta);
      });
      if (row.children.length) extra.appendChild(row);
    }

    if (!extra.children.length) return false;

    info.appendChild(extra);

    var shade = document.createElement('span');
    shade.className = 'pdp-stream-shade';
    shade.setAttribute('aria-hidden', 'true');
    image.insertBefore(shade, info);

    // Each button goes to its own address rather than the tile's
    extra.addEventListener('click', function (event) {
      var cta = event.target.closest('[data-href]');
      if (!cta) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.href = cta.getAttribute('data-href');
    });

    return true;
  }

  function enhance(card) {
    if (card.dataset.streamHoverBound === 'true') return;
    card.dataset.streamHoverBound = 'true';

    if (!card.querySelector('.pdp-stream-extra') && !buildExtra(card)) return;

    var info = card.querySelector('.card-info');
    // Logline + buttons, revealed as one
    var extra = card.querySelector('.pdp-stream-extra');
    var shade = card.querySelector('.pdp-stream-shade');
    var img = card.querySelector('.card-image img');
    if (!info || !extra) return;

    // Shows the extra block (CSS) so it can be measured, and retires the old
    // scale-up and dropdown panel
    card.classList.add('stream-tile--gsap');

    // Opening and closing are separate tweens rather than one timeline
    // played backwards: reversed, an ease-out becomes an ease-in, so the
    // text would accelerate and land with a thud. Each direction gets its
    // own ease-out and starts from wherever the last one had got to.
    var restY = 0;

    function setRest() {
      restY = extra.offsetHeight;
      gsap.set(info, { y: restY });
      gsap.set(extra, { autoAlpha: 0 });
      if (shade) gsap.set(shade, { opacity: 0 });
      if (img) gsap.set(img, { scale: 1, transformOrigin: '50% 50%' });
    }

    setRest();

    var isOpen = false;

    // Buttons: the PDP Add to cart's hover in miniature, played as part of
    // the tile hover. Once they've risen into place, the pink fades in
    // softly and the arrows nudge up-right; on leave the pink fades out.
    var fills = card.querySelectorAll('.pdp-stream-cta-fill');
    var arrows = card.querySelectorAll('.pdp-stream-cta .watch-now-arrow');
    if (fills.length) gsap.set(fills, { scaleX: 1, opacity: 0 });

    function ctaIn() {
      if (!fills.length) return;
      gsap.to(fills, { opacity: 1, duration: 0.2, delay: 0.2, ease: 'power1.out', overwrite: true });
      if (arrows.length) gsap.to(arrows, { x: 2, y: -2, duration: 0.4, delay: 0.2, ease: 'power2.out', overwrite: true });
    }

    function ctaOut() {
      if (!fills.length) return;
      gsap.to(fills, { opacity: 0, duration: 0.2, ease: 'power1.out', overwrite: true });
      if (arrows.length) gsap.to(arrows, { x: 0, y: 0, duration: 0.35, ease: 'power2.out', overwrite: true });
    }

    function open() {
      isOpen = true;
      gsap.to(info, { y: 0, duration: 0.7, ease: 'power3.out', overwrite: 'auto' });
      // The logline rides up inside the same block as the title, so it
      // moves at exactly the same speed; it only fades in alongside.
      gsap.to(extra, { autoAlpha: 1, duration: 0.7, ease: 'power3.out', overwrite: 'auto' });
      if (shade) gsap.to(shade, { opacity: 1, duration: 0.6, ease: 'power2.out', overwrite: 'auto' });
      if (img) gsap.to(img, { scale: 1.04, duration: 1.1, ease: 'power2.out', overwrite: 'auto' });
      ctaIn();
    }

    function close() {
      isOpen = false;
      // The logline fades gently as the title settles back down, both
      // easing out together (it rides down inside the same block).
      gsap.to(extra, { autoAlpha: 0, duration: 0.7, ease: 'power2.inOut', overwrite: 'auto' });
      gsap.to(info, { y: restY, duration: 0.7, ease: 'power3.out', overwrite: 'auto' });
      if (shade) gsap.to(shade, { opacity: 0, duration: 0.55, ease: 'power2.out', overwrite: 'auto' });
      if (img) gsap.to(img, { scale: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
      ctaOut();
    }

    card.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') open();
    });
    card.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') close();
    });
    card.addEventListener('focusin', open);
    card.addEventListener('focusout', function (event) {
      if (!card.contains(event.relatedTarget)) close();
    });

    // Tile widths follow the window, and the logline's height with them,
    // so re-measure (keeping the current state) after a resize.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        setRest();
        if (isOpen) open();
      }, 120);
    });
  }

  function init(scope) {
    if (typeof gsap === 'undefined' || reduceMotion || !finePointer.matches) return;
    var cards = (scope || document).querySelectorAll('.product-card.stream-block');
    Array.prototype.forEach.call(cards, enhance);
  }

  // For tiles added after load
  window.streamTileHover = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
